import { useEffect, useState } from "react";
import UserLayout from "@/components/UserLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, ArrowRight, ShoppingBag, Wallet, Zap, Phone, Sparkles, Globe2, ShieldCheck, RefreshCw, Clock, Copy, TrendingUp, Users } from "lucide-react";
import { ServiceIcon } from "@/components/ServiceIcon";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [numbers, setNumbers] = useState([]);
  const [txs, setTxs] = useState([]);
  const [services, setServices] = useState([]);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    refresh();
    Promise.all([
      api.get("/numbers/my"),
      api.get("/transactions/my"),
      api.get("/services"),
      api.get("/countries"),
    ]).then(([n, t, s, c]) => {
      setNumbers(n.data);
      setTxs(t.data);
      setServices(s.data);
      setCountries(c.data);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const activeCount = numbers.filter((n) => n.status === "waiting" || n.status === "received").length;
  const receivedCount = numbers.filter((n) => n.sms_code).length;
  const totalSpent = numbers.reduce((s, n) => s + Number(n.price || 0), 0);
  const displayName = user?.first_name || user?.username || "kullanıcı";

  const copy = (t) => {
    navigator.clipboard.writeText(t);
    toast.success("Kopyalandı");
  };

  const popularServices = services.slice(0, 8);

  return (
    <UserLayout>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 border border-[hsl(217,91%,60%)]/40 bg-[hsl(217,91%,60%)]/10 text-[hsl(217,91%,68%)] px-3 py-1 rounded-full text-xs font-bold mb-3">
            <Zap className="w-3 h-3" /> {new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" })}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black">Merhaba, {displayName} 👋</h1>
          <p className="text-white/60 text-sm md:text-base mt-2">
            Sanal numara al, SMS kodunu bekle, 20 dakikada otomatik iade güvencesiyle risksiz kullan.
          </p>
        </div>
      </div>

      {/* Hero: Balance + Quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="card-glow rounded-3xl p-6 md:p-7 lg:col-span-2" data-testid="home-balance-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-white/80 text-sm font-medium">Mevcut bakiye</div>
              <div className="font-display font-black text-white text-5xl md:text-6xl leading-tight mt-1">
                ₺{Number(user?.balance || 0).toFixed(2)}
              </div>
              <div className="text-white/70 text-xs mt-2 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" /> 20dk otomatik iade koruması aktif
              </div>
            </div>
            <div className="hidden md:flex flex-col items-end text-right">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/70">Toplam Harcama</span>
              <span className="font-mono text-lg text-white font-bold">₺{totalSpent.toFixed(2)}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link to="/deposit" className="inline-flex items-center gap-2 bg-white text-[hsl(217,91%,45%)] font-bold text-sm px-5 py-3 rounded-2xl hover:brightness-110 transition-all" data-testid="home-deposit">
              <Plus className="w-4 h-4" /> Bakiye yükle
            </Link>
            <Link to="/buy" className="inline-flex items-center gap-2 bg-white/15 backdrop-blur text-white font-bold text-sm px-5 py-3 rounded-2xl border border-white/25 hover:bg-white/20 transition-all" data-testid="home-buy">
              <ShoppingBag className="w-4 h-4" /> Numara al
            </Link>
          </div>
        </div>

        <div className="border border-white/10 bg-[hsl(0,0%,7%)] rounded-3xl p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Hesap Özeti</div>
          <SummaryRow label="Aktif numara" value={activeCount} icon={<Phone className="w-4 h-4" />} />
          <SummaryRow label="Alınan SMS" value={receivedCount} icon={<Sparkles className="w-4 h-4" />} />
          <SummaryRow label="Toplam işlem" value={txs.length} icon={<Wallet className="w-4 h-4" />} />
          <Link to="/numbers" className="mt-3 inline-flex items-center gap-1 text-xs text-[hsl(217,91%,68%)] hover:text-white transition-colors" data-testid="link-all-activity">
            Tüm aktiviteler <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Popular services quick-buy */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display font-black text-2xl">Popüler Servisler</h2>
            <p className="text-white/50 text-sm">Tek tıkla numara satın alma sayfasına git.</p>
          </div>
          <Link to="/buy" className="text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white transition-colors">
            Tümü →
          </Link>
        </div>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
          {popularServices.map((s) => (
            <Link key={s.code} to="/buy" className="border border-white/10 bg-[hsl(0,0%,7%)] hover:border-[hsl(217,91%,60%)]/60 rounded-2xl p-3 flex flex-col items-center gap-2 transition-colors" data-testid={`popular-${s.code}`}>
              <ServiceIcon code={s.code} size="md" />
              <div className="text-[10px] text-white/70 uppercase tracking-wider text-center leading-tight truncate w-full">{s.name.split("/")[0]}</div>
            </Link>
          ))}
        </div>
      </div>

      {/* 3-column feature grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <FeatureCard icon={<Globe2 className="w-5 h-5" />} title="50+ Ülke" desc="Türkiye, ABD, Rusya, Almanya ve daha fazlası. Dilediğiniz bölgeden numara alın." accent="hsl(217,91%,60%)" />
        <FeatureCard icon={<RefreshCw className="w-5 h-5" />} title="Otomatik İade" desc="20 dakika içinde SMS gelmezse ücretiniz otomatik olarak iade edilir." accent="hsl(142,71%,55%)" />
        <FeatureCard icon={<ShieldCheck className="w-5 h-5" />} title="Güvenli & Anonim" desc="Numaralar tek kullanımlıktır. Kişisel bilginiz asla paylaşılmaz." accent="hsl(0,84%,60%)" />
      </div>

      {/* Recent numbers + Recent transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-xl">Son Numaralar</h2>
            <Link to="/numbers" className="text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white transition-colors" data-testid="link-all-numbers">
              Tümü →
            </Link>
          </div>
          {numbers.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-2xl p-8 text-center text-sm text-white/50">
              Henüz numara almadınız. <Link to="/buy" className="text-[hsl(217,91%,68%)]">Şimdi başla</Link>
            </div>
          ) : (
            <div className="space-y-2">
              {numbers.slice(0, 5).map((n) => (
                <div key={n.id} className="border border-white/10 bg-[hsl(0,0%,7%)] rounded-2xl p-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <ServiceIcon code={n.service} size="sm" />
                    <div className="min-w-0">
                      <button onClick={() => copy(n.phone)} className="font-mono font-bold truncate inline-flex items-center gap-1.5 hover:text-[hsl(217,91%,68%)]">
                        +{n.phone} <Copy className="w-3 h-3 text-white/40" />
                      </button>
                      <div className="text-xs text-white/50 truncate">{n.country_name}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-sm">₺{Number(n.price).toFixed(2)}</div>
                    <StatusMini s={n.status} sms={n.sms_code} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-xl">Son Bakiye İşlemleri</h2>
            <Link to="/deposit" className="text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white transition-colors" data-testid="link-deposit">
              Yükle →
            </Link>
          </div>
          {txs.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-2xl p-8 text-center text-sm text-white/50">
              Henüz bakiye işleminiz yok.
            </div>
          ) : (
            <div className="space-y-2">
              {txs.slice(0, 5).map((t) => (
                <div key={t.id} className="border border-white/10 bg-[hsl(0,0%,7%)] rounded-2xl p-4 flex items-center justify-between">
                  <div>
                    <div className="font-mono font-bold">₺{Number(t.amount).toFixed(2)}</div>
                    <div className="text-xs text-white/50 uppercase tracking-wider">{t.method}</div>
                  </div>
                  <TxStatus s={t.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </UserLayout>
  );
}

function SummaryRow({ label, value, icon }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 last:border-0 pb-3 last:pb-0">
      <span className="text-sm text-white/60 inline-flex items-center gap-2">
        <span className="text-white/40">{icon}</span>{label}
      </span>
      <span className="font-mono font-bold text-white text-lg">{value}</span>
    </div>
  );
}

function FeatureCard({ icon, title, desc, accent }) {
  return (
    <div className="border border-white/10 bg-[hsl(0,0%,7%)] rounded-2xl p-5 hover:border-white/25 transition-colors">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${accent}22`, color: accent }}>
        {icon}
      </div>
      <div className="font-display font-bold text-lg mb-1">{title}</div>
      <div className="text-white/60 text-sm leading-relaxed">{desc}</div>
    </div>
  );
}

function StatusMini({ s, sms }) {
  const map = {
    waiting: { label: sms ? "Bekliyor" : "SMS Bekliyor", cls: "text-[hsl(38,92%,60%)]" },
    received: { label: "Geldi", cls: "text-[hsl(142,71%,55%)]" },
    cancelled: { label: "İade", cls: "text-white/40" },
    finished: { label: "Tamam", cls: "text-white/50" },
  };
  const m = map[s] || map.waiting;
  return <div className={`text-[10px] uppercase tracking-[0.15em] font-bold ${m.cls}`}>{m.label}</div>;
}

function TxStatus({ s }) {
  const map = {
    0: { label: "Bekliyor", cls: "text-[hsl(38,92%,60%)] border-[hsl(38,92%,60%)]/40" },
    1: { label: "Onay", cls: "text-[hsl(142,71%,55%)] border-[hsl(142,71%,55%)]/40" },
    2: { label: "Red", cls: "text-red-400 border-red-500/40" },
  };
  const m = map[s] || map[0];
  return <span className={`border px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold rounded ${m.cls}`}>{m.label}</span>;
}
